import { FormApi } from "final-form";
import { observer } from "mobx-react-lite";

import { Stack } from "@bps/fluent-ui";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { ComboBoxField } from "@ui-components/form/ComboBoxField.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

import { EditBaseIntervalModalValidator } from "./EditBaseIntervalModalValidator.ts";

export interface EditBaseIntervalFormValues {
  apptTypeBaseInterval: string;
  readKnowledgeBase: boolean;
  hasPlan: boolean;
  contactedSupport: boolean;
}

const nameOf = nameOfFactory<EditBaseIntervalFormValues>();

interface EditBaseIntervalModalProps {
  hidden: boolean;
  currentValue: string;
  onDismiss: () => void;
}

const validator = new EditBaseIntervalModalValidator();

export const EditBaseIntervalModal: React.FC<EditBaseIntervalModalProps> =
  observer(({ hidden, currentValue, onDismiss }) => {
    const { core, notification, userExperience } = useStores();
    const options = userExperience.ref.apptTypeBaseIntervals.keyTextValues;

    if (hidden) {
      return null;
    }

    const onSubmit = async (
      values: EditBaseIntervalFormValues,
      form: FormApi<EditBaseIntervalFormValues>
    ) => {
      await Promise.all(
        core.allLocations.map(location =>
          userExperience.updateOrgUnitSetting({
            orgUnitId: location.id,
            appointmentSetting: {
              apptTypeBaseInterval: values.apptTypeBaseInterval,
              updateUpcomingApptDuration: false
            },
            eTag: userExperience.orgUnitSettingMap.get(location.id)?.eTag || ""
          })
        )
      );
      notification.success(
        "Appointment length base interval has been updated."
      );
      form.restart();
      onDismiss();
    };

    return (
      <SubmissionFormDialog<EditBaseIntervalFormValues>
        dialogName="edit-base-interval"
        autoFocus={false}
        validate={values => validator.validate(values)}
        onSubmit={onSubmit}
        initialValues={{
          apptTypeBaseInterval: currentValue,
          readKnowledgeBase: false,
          hasPlan: false,
          contactedSupport: false
        }}
        dialogProps={{
          onDismiss,
          maxWidth: 600,
          dialogContentProps: {
            title: "Edit appointment length base interval"
          }
        }}
        buttonsProps={{
          disableSubmitOnFormInvalid: true,
          hideButtonsSeparator: true
        }}
      >
        {() => (
          <Stack tokens={{ childrenGap: 12 }}>
            <ComboBoxField
              name={nameOf("apptTypeBaseInterval")}
              label="Base interval (min)"
              options={options}
              styles={{ root: { width: 200 } }}
              useComboBoxAsMenuWidth
              required
            />
            <CheckboxField
              name={nameOf("readKnowledgeBase")}
              label="I have read the knowledge base, and understand this change will impact my appointments and online booking"
            />
            <CheckboxField
              name={nameOf("hasPlan")}
              label="I have a plan to minimise impact to my practice, and manage changes caused by this change"
            />
            <CheckboxField
              name={nameOf("contactedSupport")}
              label="I have contacted Best Practice support with any questions I may have had"
            />
          </Stack>
        )}
      </SubmissionFormDialog>
    );
  });
